import moment from 'moment';
import { DeliveryBlock, DeliveryBlockResourceLocationType, Driver, ParkingSpot, User, Vehicle } from '@bringg/types';
import type { EtaResponseRouteItem } from '@bringg-frontend/eta-calculator';
import type { MarkerData } from '@bringg/react-components/dist/types/maps.consts';
import type { Directions } from '@bringg/types/types/directions';

import type { RoutesPlannerTranslations } from '../../../components';
import type { IconsProps, StartEndMarker } from '../../../components/routes-planner/routes-planner';
import type { RoutesPlannerRun } from '../../../types';
import { getResourceDeliveryBlocks } from '../../../components/routes-planner/timeline-logic/delivery-block-logic';
import { isValidLatLng } from '../../../utils/map-utils';

const arrangeRoutesStartEndMarkers = ({
	directions,
	routesById,
	routeInfoByIndex,
	drivers,
	parkingSpots,
	translations,
	vehicles,
	deliveryBlocksByDriver,
	deliveryBlocksByVehicle
}: {
	directions: Directions[];
	routesById: { [run_id: number]: RoutesPlannerRun };
	routeInfoByIndex: { [runId: number]: EtaResponseRouteItem };
	drivers: { [user_id: number]: Driver };
	parkingSpots: ParkingSpot[];
	translations: RoutesPlannerTranslations;
	vehicles: Vehicle[];
	deliveryBlocksByDriver: { [userId: number]: DeliveryBlock[] };
	deliveryBlocksByVehicle: { [vehicleId: number]: DeliveryBlock[] };
}): Map<number, { startMarker: StartEndMarker; endMarker: StartEndMarker }> => {
	const routesData = new Map<number, { startMarker: StartEndMarker; endMarker: StartEndMarker }>();

	directions.forEach(direction => {
		const runId = direction.run_id;
		const run = routesById[runId];
		const runByRouteInfo = routeInfoByIndex[runId];
		const runStartTime = moment((runByRouteInfo?.depot_etl || run?.depot_etl) * 1000).toISOString();
		const runEndTime = moment((runByRouteInfo?.depot_back_eta || run.depot_back_eta) * 1000).toISOString();
		const driverId = runByRouteInfo?.user_id || run.user_id;
		const vehicleId = runByRouteInfo?.power_unit_id || run.vehicle_id;

		const deliveryBlock = getResourceDeliveryBlocks(
			run,
			deliveryBlocksByDriver,
			deliveryBlocksByVehicle,
			runStartTime,
			runEndTime
		)[0];

		const resource = deliveryBlock?.delivery_block_resources?.find(
			resource =>
				(resource.vehicle_id && resource.vehicle_id === vehicleId) ||
				(resource.user_id && resource.user_id === driverId)
		);
		const vehicle = vehicles.find(vehicle => vehicle.id === vehicleId);
		const vehicleDefaultSpotId = vehicle?.default_parking_spot_id;
		if (resource) {
			const markerData = {
				startMarker: getMarkerDataFromResource(
					resource.start_location_type,
					driverId,
					drivers,
					vehicleDefaultSpotId,
					resource.start_parking_spot_id,
					parkingSpots,
					translations
				),
				endMarker: getMarkerDataFromResource(
					resource.end_location_type,
					driverId,
					drivers,
					vehicleDefaultSpotId,
					resource.end_parking_spot_id,
					parkingSpots,
					translations
				)
			};
			routesData.set(runId, markerData);
		}
	});
	return routesData;
};

const getMarkerDataFromResource = (
	locationType: DeliveryBlockResourceLocationType,
	driver: number,
	users: { [user_id: number]: User },
	vehicleDefaultSpotId: number,
	parkingSpotId: number,
	parkingSpots: ParkingSpot[],
	translations: RoutesPlannerTranslations
): StartEndMarker => {
	let lat;
	let lng;
	let title;
	let parkingSpot;
	switch (locationType) {
		case DeliveryBlockResourceLocationType.DRIVER_ADDRESS:
			if (users[driver]) {
				const driversFirstName = users[driver].name.split(' ')[0];
				const driversTitle = translations.tooltipTranslations.home(driversFirstName);
				lat = users[driver].home_lat;
				lng = users[driver].home_lng;
				title = driversTitle;
			}
			break;

		case DeliveryBlockResourceLocationType.PARKING_SPOT:
			parkingSpot = parkingSpots?.find(park => park.id === parkingSpotId);
			if (parkingSpot) {
				lat = parkingSpot.lat;
				lng = parkingSpot.lng;
				title = parkingSpot.name;
			}
			break;

		case DeliveryBlockResourceLocationType.VEHICLE_DEFAULT_PARKING:
			parkingSpot = parkingSpots?.find(park => park.id === vehicleDefaultSpotId);
			if (parkingSpot) {
				lat = parkingSpot.lat;
				lng = parkingSpot.lng;
				title = parkingSpot.name;
			}
			break;

		default:
			return null;
	}

	if (!isValidLatLng(lat, lng)) {
		return null;
	}

	return { lat, lng, type: locationType, title };
};

const createStartOrEndMarker = (
	type: string,
	markerData: StartEndMarker,
	color: string,
	icons: IconsProps,
	runId: number
): MarkerData => {
	const iconByType =
		markerData.type === DeliveryBlockResourceLocationType.DRIVER_ADDRESS ? icons.driver : icons.parking;
	const iconWrapperId = color + markerData.title;

	return {
		id: `${type}-marker-run-${runId}-type-${markerData.type}`,
		location: {
			lat: markerData.lat,
			lng: markerData.lng
		},
		icon: iconByType.icon(color, markerData.title, iconWrapperId),
		iconOptions: iconByType.iconOptions
	};
};

export const markersProvider = { arrangeRoutesStartEndMarkers, createStartOrEndMarker };
